import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Article {
  slug: string;
  title: string;
  subtitle: string;
  category: string;
  author: string;
  published_at: string;
  tags: string[];
  body_markdown: string;
  cta_type?: string;
  cta_text?: string;
}

// Hardcoded articles for fallback
const FALLBACK_ARTICLES: Record<string, Article> = {
  'slucajnost-od-54000-eura': {
    slug: 'slucajnost-od-54000-eura',
    title: '"Slučajnost" od 54.000 eura: Kako izgleda zakon za elitu',
    subtitle: 'Martina Dalić prekršila Zakon o tržištu kapitala. Kazna? Javno upozorenje. Mali Perica nije platio HRT? Ovrha.',
    category: 'javne-usluge',
    author: 'Igor Grčman',
    published_at: '2025-12-18',
    tags: ['hanfa', 'dalic', 'matejev-efekt', 'stratifikacija', 'zakon'],
    cta_type: 'ei_quiz',
    cta_text: 'Jesi li Dalić ili Perica? Riješi Extraction Index Kviz',
    body_markdown: `Zamisli da "slučajno" prođeš kroz crveno na Vukovarskoj. Policajac te zaustavi, a ti kažeš: *"Oprosti, nisam htio. Mogu se vratiti u rikverc?"*

Bi li ti oprostili kaznu? Naravno da ne.

Ali ako si na vrhu hranidbenog lanca, stvari funkcioniraju drugačije.

## Što se dogodilo?

Martina Dalić, predsjednica Uprave Podravke (bivša potpredsjednica Vlade), kupila je dionice vlastite tvrtke u vrijeme kad je to **strogo zabranjeno**.

Zakon je jasan: rukovoditelji ne smiju trgovati dionicama 30 dana prije objave financijskih rezultata. To se zove *blackout period* - postoji da bi se spriječilo insajdersko trgovanje.

Dalić je kupila 350 dionica za **54.432 eura** - točno dva tjedna prije objave rezultata.

Njena obrana? *"Nenamjerna greška."*

## Što kaže zakon vs. što je dobila?

Za ovaj prekršaj zakon predviđa kaznu od **6.630 do 500.000 eura**.

Ako se dokaže insajdersko trgovanje, kazna može biti od šest mjeseci do pet godina zatvora.

Dalić je dobila: **Javno upozorenje.**

Čitaš dobro. Nije dobila minimalnih 6.630€. Nije dobila proporcionalno izračunatu kaznu prema njenoj poziciji i primanjima. Dobila je - ništa. Upozorenje. Besplatno.

## Stavimo u perspektivu

| Što | Iznos |
|-----|-------|
| Minimalna zakonska kazna | 6.630 € |
| Dalićin godišnji bonus | ~230.000 € |
| Minimalna kazna kao % bonusa | 2.9% |
| **STVARNA KAZNA** | **0 €** |

Čak i da je platila zakonski minimum, to bi njoj bilo kao da ti dobiješ kaznu od **30€** za prolazak kroz crveno - uz plaću od 1.000€.

Ali nije platila ni to. Platila je **ništa**.

## Proporcionalna pravda?

U nekim zemljama (Finska, Švicarska) prometne kazne se računaju prema primanjima. Multimilijunaš plati više za prebrzu vožnju nego student.

Zašto? Jer 50€ kazne za nekoga s 1.000€ plaće i za nekoga s 50.000€ plaće - **nije ista kazna**.

Za Martinu Dalić, čak i maksimalna kazna od 500.000€ bila bi otprilike 2 godine bonusa. Bolna, ali preživljiva.

Za malog Pericu s 1.200€ plaće, kazna od 6.630€ = **5.5 mjesečnih plaća**. Katastrofa.

Ali ona nije dobila ni minimum. Dobila je riječi na papiru.

## Poruka sustava

Sociolozi to zovu **Matejev efekt**: *"Tko ima, dat će mu se još više."*

Originalni biblijski nastavak je još okrutniji i savršeno opisuje Hrvatsku: *"...a tko nema, uzet će mu se i ono što ima."*

**Sustav nije dizajniran da bude pravedan. Dizajniran je da štiti one koji ga već imaju.**

---

## 👉 GUKNI BROJKOM

| Situacija | Martina Dalić | Mali Perica |
|-----------|---------------|-------------|
| Prekršaj | 54.000€ (nedozvoljena kupnja) | 50€ (neplaćeni HRT) |
| Kazna po zakonu | 6.630€ - 500.000€ | Ovrha + Blokada računa |
| **STVARNA KAZNA** | **0€** (Javno upozorenje) | **150€+** (Ovrha, FINA, bilježnik) |

*"Tko ima - dat će mu se. Tko nema - uzet će mu se."*

---

*Izvori: HANFA rješenje (prosinac 2025), Zakon o tržištu kapitala RH*`
  },
  'spijun-u-dzepu-banka-kazna': {
    slug: 'spijun-u-dzepu-banka-kazna',
    title: 'Špijun u džepu: Banka ti je napravila digitalni pretres (a nisi ni znao)',
    subtitle: '1,5 milijuna eura kazne jer su znali sve o tebi. Tvoja cijena? 3,46 eura.',
    category: 'digitalna-pismenost',
    author: 'Igor Grčman',
    published_at: '2025-12-18',
    tags: ['azop', 'banke', 'privatnost', 'gdpr'],
    cta_type: 'ei_quiz',
    cta_text: 'Koliko vrijediš sustavu? Riješi Extraction Index Kviz',
    body_markdown: `Pišem iz Bogote, grada koji ima reputaciju "opasnog". Ovdje te na ulici mogu opljačkati dečki na motoru. Traže mobitel i novčanik. To se zove kriminal.

U Hrvatskoj te pljačka banka, preko aplikacije koju si sam instalirao. To se zove "bankarstvo".

Agencija za zaštitu osobnih podataka (AZOP) upravo je "odrezala" jednu od najvećih kazni u povijesti Hrvatske: **1,5 milijuna eura** jednoj banci.

Razlog? **Masovna, ilegalna digitalna špijunaža.**

## Što su radili?

Banka je u svoju aplikaciju za mobilno bankarstvo (Android i Huawei) ugradila kod koji je skenirao **SVE druge aplikacije** na tvom mobitelu. Popis tih aplikacija slali su u svoju centralnu bazu. To su radili na **433.922 korisnika**.

## Zašto je to opasno?

Možda misliš: *"Pa što ima veze ako znaju da imam WhatsApp?"*

Ali tvoj popis aplikacija je tvoj **digitalni otisak prsta**. On govori tko si kad nitko ne gleda:

* **Kockarske aplikacije (SuperSport, PSK):** Banka zna da si rizičan. Možda ti odbiju kredit ili dignu kamatu.
* **Fintech (Revolut, Aircash):** Znaju da koristiš konkurenciju.
* **Zdravstvene aplikacije:** Znaju tvoje stanje (npr. praćenje šećera, onkologija).
* **Dating aplikacije (Tinder, Grindr):** Znaju tvoj status i orijentaciju.

AZOP jasno kaže: *"Pojedine aplikacije mogu otkrivati posebne kategorije podataka (zdravlje, politička uvjerenja, seksualna orijentacija)."*

## Službena laž

Kada su ih uhvatili, banka se branila klasičnim korporativnim blefom: *"To je zbog sigurnosti."*

AZOP je to srušio. Zakon ne traži da znate ima li klijent instaliran Tinder da bi mu procesirali uplatu struje.

Prava istina? Podaci su **nova nafta**. Banka je htjela besplatno crpiti tu naftu da bi radila bolje **rizične profile** i prodavala ti skuplje proizvode.

## Poruka sustava

Sociolozi to zovu **Matejev efekt**: *"Tko ima, dat će mu se još više."*

Banka ima podatke, moć i odvjetnike. Ti imaš - račun za usluge koje ti naplaćuju dok te špijuniraju.

---

## 👉 GUKNI BROJKOM: Cijena tvoje intime

Kada podijelimo iznos kazne s brojem špijuniranih ljudi, dolazimo do poražavajuće istine.

| Što | Iznos |
|-----|-------|
| Broj špijuniranih korisnika | 433.922 |
| Kazna za banku | 1.500.000 € |
| **KAZNA PO KORISNIKU** | **3,46 €** |
| Koliko su platili tebi? | **0 €** |

**3,46 eura.** Toliko vrijedi tvoja privatnost. Manje od kave i soka u centru grada.

---

## Usporedba: Matejev efekt na djelu

| Situacija | Banka | Ti |
|-----------|-------|-----|
| Prekršaj | Špijunira 433.922 ljudi godinama | Kasniš 1 dan s ratom kredita |
| Kazna | 1.5M € (trošak poslovanja) | Opomena + kamata + HROK |
| Posljedica | Nastavljaju dalje, bonusi se isplaćuju | "Crna lista", nema kredita 3 godine |

---

## Zaključak iz Bogote

Ovdje u Kolumbiji, kriminalci barem imaju pristojnosti da nose maske. U Hrvatskoj nose odijela, sjede u upravama i kad ih uhvate s rukama u tvom digitalnom džepu, plate kaznu od 3 eura po glavi.

Sljedeći put kad ti banka pošalje "personaliziranu ponudu", zapitaj se: Jesu li je skrojili prema onome što vide na tvom tekućem računu, ili prema onome što su vidjeli na tvom ekranu dok si mislio da si sam?

---

*Izvori: AZOP službeno priopćenje (prosinac 2025)*`
  }
};

function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
    }
  }, [slug]);

  const fetchArticle = async (articleSlug: string) => {
    try {
      const response = await fetch(`/api/articles/${articleSlug}`);
      if (!response.ok) throw new Error('Članak nije pronađen');
      const data = await response.json();
      // API returns article directly, not wrapped in {article: ...}
      setArticle(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nepoznata greška');
      // Fallback to hardcoded article
      if (articleSlug && FALLBACK_ARTICLES[articleSlug]) {
        setArticle(FALLBACK_ARTICLES[articleSlug]);
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, string> = {
      'proracun-i-porezi': 'Proračun i porezi',
      'javne-usluge': 'Javne usluge',
      'stanovanje-i-zivot': 'Stanovanje i život',
      'digitalna-pismenost': 'Digitalna pismenost'
    };
    return categories[category] || category;
  };

  if (loading) {
    return (
      <div className="article-page">
        <div className="loading">Učitavam članak...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-page">
        <div className="error-page">
          <h1>Članak nije pronađen</h1>
          <p>{error}</p>
          <Link to="/clanci" className="btn btn-primary">← Natrag na članke</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="article-page">
      <article className="article">
        <header className="article-header">
          <div className="article-meta-top">
            <Link to="/clanci" className="back-link">← Svi članci</Link>
            <span className="article-category">{getCategoryLabel(article.category)}</span>
          </div>
          
          <h1 className="article-title">{article.title}</h1>
          <p className="article-subtitle">{article.subtitle}</p>
          
          <div className="article-meta">
            <span className="article-author">
              <strong>{article.author}</strong>
              {article.author === 'Igor Grčman' && <span className="author-location"> (Bogotá)</span>}
            </span>
            <span className="article-date">
              {new Date(article.published_at).toLocaleDateString('hr-HR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </header>

        <div className="article-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body_markdown}</ReactMarkdown>
        </div>

        {article.cta_type === 'ei_quiz' && (
          <div className="article-cta">
            <p>{article.cta_text || 'Saznaj svoj ekonomski profil'}</p>
            <Link to="/kviz" className="btn btn-primary">
              Riješi Extraction Index Kviz →
            </Link>
          </div>
        )}

        <footer className="article-footer">
          <div className="article-tags">
            {article.tags?.map(tag => (
              <span key={tag} className="tag">#{tag}</span>
            ))}
          </div>
          
          <div className="article-share">
            <span>Podijeli:</span>
            <a 
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="share-btn"
            >
              Twitter
            </a>
            <a 
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="share-btn"
            >
              LinkedIn
            </a>
          </div>
        </footer>
      </article>

      <nav className="article-nav">
        <Link to="/clanci" className="btn btn-secondary">← Svi članci</Link>
        <Link to="/kviz" className="btn btn-primary">Riješi EI Kviz →</Link>
      </nav>
    </div>
  );
}

export default ArticlePage;
